import { create } from 'zustand';
import axios from 'axios';
import { tokenStore } from '@/lib/auth';
import { hydrationSignal } from '@/lib/hydration';
import { api } from '@/lib/api';
import type { AuthResponse, OrgSummary, User } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isInitializing: boolean;
  activeOrgId: string | null;
  organizations: OrgSummary[];
  setSession: (resp: AuthResponse) => void;
  clear: () => void;
  hydrate: () => Promise<void>;
  setUser: (user: User | null) => void;
  setActiveOrg: (orgId: string | null) => void;
}

const USER_KEY = 'bb.user';
const ACTIVE_ORG_KEY = 'bb.activeOrgId';
const ORGS_KEY = 'bb.organizations';

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(u: User | null): void {
  try {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

function readStoredActiveOrgId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStoredActiveOrgId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_ORG_KEY, id);
    else localStorage.removeItem(ACTIVE_ORG_KEY);
  } catch {
    /* ignore */
  }
}

function readStoredOrgs(): OrgSummary[] {
  try {
    const raw = localStorage.getItem(ORGS_KEY);
    return raw ? (JSON.parse(raw) as OrgSummary[]) : [];
  } catch {
    return [];
  }
}

function writeStoredOrgs(orgs: OrgSummary[]): void {
  try {
    if (orgs.length > 0) localStorage.setItem(ORGS_KEY, JSON.stringify(orgs));
    else localStorage.removeItem(ORGS_KEY);
  } catch {
    /* ignore */
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: readStoredUser(),
  accessToken: null,
  isInitializing: true,
  activeOrgId: readStoredActiveOrgId(),
  organizations: readStoredOrgs(),
  setSession: (resp) => {
    tokenStore.setAccess(resp.access_token);
    tokenStore.setRefresh(resp.refresh_token);
    writeStoredUser(resp.user);
    const orgs = resp.organizations ?? [];
    writeStoredOrgs(orgs);
    // Auto-set activeOrgId if exactly one org, preserve existing if still valid
    let activeOrgId = readStoredActiveOrgId();
    if (orgs.length === 1) {
      activeOrgId = orgs[0].id;
    } else if (activeOrgId && !orgs.find((o) => o.id === activeOrgId)) {
      // Previously selected org is no longer in membership list — clear it
      activeOrgId = null;
    }
    writeStoredActiveOrgId(activeOrgId);
    set({ user: resp.user, accessToken: resp.access_token, isInitializing: false, organizations: orgs, activeOrgId });
  },
  setUser: (user) => {
    writeStoredUser(user);
    set({ user });
  },
  setActiveOrg: (orgId) => {
    writeStoredActiveOrgId(orgId);
    set({ activeOrgId: orgId });
  },
  clear: () => {
    tokenStore.clear();
    writeStoredUser(null);
    writeStoredActiveOrgId(null);
    writeStoredOrgs([]);
    set({ user: null, accessToken: null, isInitializing: false, activeOrgId: null, organizations: [] });
  },
  hydrate: async () => {
    // Use raw axios (not `api`) so the response interceptor doesn't
    // redirect to /login when the refresh token is expired/invalid — that
    // would break public pages like /password-reset/confirm and /accept-invite.
    const refresh = tokenStore.getRefresh();
    if (!refresh) {
      set({ isInitializing: false });
      hydrationSignal.resolve();
      return;
    }
    try {
      const r = await axios.post<{ access_token: string; refresh_token?: string; user?: User; organizations?: OrgSummary[] }>(
        `${BASE_URL}/auth/refresh`,
        { refresh_token: refresh },
        { headers: { 'Content-Type': 'application/json' } },
      );
      tokenStore.setAccess(r.data.access_token);
      if (r.data.refresh_token) tokenStore.setRefresh(r.data.refresh_token);
      const orgs = r.data.organizations ?? readStoredOrgs();
      writeStoredOrgs(orgs);
      let activeOrgId = readStoredActiveOrgId();
      if (orgs.length === 1 && !activeOrgId) {
        activeOrgId = orgs[0].id;
        writeStoredActiveOrgId(activeOrgId);
      } else if (activeOrgId && orgs.length > 0 && !orgs.find((o) => o.id === activeOrgId)) {
        activeOrgId = null;
        writeStoredActiveOrgId(null);
      }
      if (r.data.user) {
        writeStoredUser(r.data.user);
        set({ user: r.data.user, accessToken: r.data.access_token, isInitializing: false, organizations: orgs, activeOrgId });
      } else {
        try {
          const me = await api.get<User>('/users/me');
          writeStoredUser(me.data);
          set({ user: me.data, accessToken: r.data.access_token, isInitializing: false, organizations: orgs, activeOrgId });
        } catch {
          set({ accessToken: r.data.access_token, isInitializing: false, organizations: orgs, activeOrgId });
        }
      }
    } catch {
      tokenStore.clear();
      writeStoredUser(null);
      writeStoredActiveOrgId(null);
      writeStoredOrgs([]);
      set({ user: null, accessToken: null, isInitializing: false, activeOrgId: null, organizations: [] });
    } finally {
      hydrationSignal.resolve();
    }
  },
}));
